import { t } from '@lingui/core/macro';
import { type AppLinkOptions, linkOptions } from '@luminovo/design-system';
import {
    ArrowUpToLineIcon,
    BellIcon,
    BoxIcon,
    CogIcon,
    FileTextIcon,
    LayersIcon,
    type LucideIcon,
    PlusIcon,
    SearchIcon,
} from 'lucide-react';

import { templateLinks } from '@/app/navigation/templateLinks';

/**
 * A single navigational command that can appear in both the Command Palette
 * and as a Quick Action on the Home page.
 *
 * A command shows where the page its `link` opens lets the user in, by that route's `staticData.access`
 * (`useOpenableCommands`); it carries no permission test of its own.
 *
 * Rules:
 * - `id`          must be stable across releases — users persist these IDs in dashboard preferences
 * - `link`        where the command navigates, built with `linkOptions()`; `go-to-templates` shows the first
 *                  template page the user may open instead of this one
 * - `icon`        a lucide-react icon component
 * - `title`       i18n label shown on both surfaces
 * - `description` optional subtitle shown in Command Palette only
 * - `keywords`    extra search terms for Command Palette only
 * - `group`       Command Palette grouping header (e.g. "Go to"); omit for top-level actions
 */
export type NavigationCommandConfig = {
    id: string;
    link: AppLinkOptions;
    icon: LucideIcon;
    title: () => string;
    description?: () => string;
    keywords?: string[];
    group?: () => string;
};

/**
 * Single source of truth for all navigational commands shared between the
 * Command Palette and the Quick Actions section on the Home page.
 *
 * To add a new command to both surfaces, add one entry here.
 *
 * Palette-only concerns (ask-lumi, the import parent grouping node) are NOT
 * listed here — they are defined inline in useNavigationCommands.ts.
 */
export const NAVIGATION_COMMANDS_CONFIG: NavigationCommandConfig[] = [
    // =========================================================================
    // CREATE ACTIONS
    // =========================================================================
    {
        id: 'create-project',
        title: () => t`New project`,
        description: () => t`Create a new project`,
        icon: PlusIcon,
        link: linkOptions({ to: '/project_creation' }),
        keywords: ['rfq', 'request', 'quotation', 'new'],
    },
    {
        id: 'create-alert',
        title: () => t`Create alert`,
        description: () => t`Create a part price or availability alert`,
        icon: BellIcon,
        link: linkOptions({ to: '/parts/alerts' }),
        keywords: ['alert', 'price', 'availability', 'notification'],
    },
    {
        id: 'add-offers',
        title: () => t`Add offers`,
        description: () => t`Manually add supplier offers`,
        icon: PlusIcon,
        link: linkOptions({ to: '/parts/offers/create' }),
        keywords: ['offer', 'custom', 'off-the-shelf', 'ots', 'manual', 'inventory', 'pdf'],
    },

    // =========================================================================
    // IMPORT ACTIONS (ids must start with "import-" so the palette adapter
    // can group them as children of the "Import..." parent node automatically)
    // =========================================================================
    {
        id: 'import-demands',
        title: () => t`Import demands`,
        description: () => t`Import demand data from a spreadsheet`,
        icon: ArrowUpToLineIcon,
        link: linkOptions({ to: '/parts/demands/importer' }),
        keywords: ['demand', 'supplier demand'],
    },
    {
        id: 'import-components',
        title: () => t`Import components`,
        description: () => t`Import internal part numbers (IPNs) from a spreadsheet`,
        icon: ArrowUpToLineIcon,
        link: linkOptions({ to: '/parts/components/importer' }),
        keywords: ['ipn', 'component', 'catalog'],
    },
    {
        id: 'import-offers',
        title: () => t`Import offers`,
        description: () => t`Import MPN-based supplier offers`,
        icon: ArrowUpToLineIcon,
        link: linkOptions({ to: '/parts/offers/csv-importer' }),
        keywords: ['offer', 'mpn', 'inventory'],
    },
    {
        id: 'import-customers',
        title: () => t`Import customers`,
        description: () => t`Import customer records from a CSV file`,
        icon: ArrowUpToLineIcon,
        link: linkOptions({ to: '/customers/importer' }),
        keywords: ['customer', 'company', 'csv'],
    },
    {
        id: 'import-suppliers',
        title: () => t`Import suppliers`,
        description: () => t`Import supplier and stock location records`,
        icon: ArrowUpToLineIcon,
        link: linkOptions({ to: '/suppliers/importer' }),
        keywords: ['supplier', 'stock location', 'csv'],
    },
    {
        id: 'import-supplier-contacts',
        title: () => t`Import supplier contacts`,
        description: () => t`Bulk import contacts for your suppliers`,
        icon: ArrowUpToLineIcon,
        link: linkOptions({ to: '/suppliers/supplier-contacts/importer' }),
        keywords: ['supplier contact', 'contact', 'bulk'],
    },
    {
        id: 'import-avl',
        title: () => t`Import approved vendor list`,
        description: () => t`Import line-card rules and approved vendor lists`,
        icon: ArrowUpToLineIcon,
        link: linkOptions({ to: '/suppliers/line-card-rules/importer' }),
        keywords: ['avl', 'approved vendor', 'line card', 'line-card'],
    },
    {
        id: 'import-users',
        title: () => t`Import users`,
        description: () => t`Import user accounts from a spreadsheet`,
        icon: ArrowUpToLineIcon,
        link: linkOptions({ to: '/settings/organization/users/importer' }),
        keywords: ['user', 'account', 'organization'],
    },

    // =========================================================================
    // GO TO (navigation pages, grouped in the Command Palette)
    // =========================================================================
    {
        id: 'go-to-assemblies',
        title: () => t`Assemblies`,
        description: () => t`View and manage all assemblies`,
        icon: LayersIcon,
        link: linkOptions({ to: '/assemblies' }),
        group: () => t`Go to`,
        keywords: ['bom', 'design'],
    },
    {
        id: 'go-to-parts',
        title: () => t`Part search`,
        description: () => t`Search for parts in the library`,
        icon: SearchIcon,
        link: linkOptions({ to: '/parts/search' }),
        group: () => t`Go to`,
        keywords: ['mpn', 'search', 'components', 'ots'],
    },
    {
        id: 'go-to-components',
        title: () => t`Components`,
        description: () => t`Browse component catalog`,
        icon: BoxIcon,
        link: linkOptions({ to: '/parts/components' }),
        group: () => t`Go to`,
        keywords: ['ipn', 'internal part numbers'],
    },
    {
        id: 'go-to-templates',
        title: () => t`Templates`,
        description: () => t`Manage sourcing and calculation templates`,
        icon: FileTextIcon,
        link: templateLinks.sourcing,
        group: () => t`Go to`,
        keywords: ['sourcing', 'calculation', 'email', 'manufacturing'],
    },
    {
        id: 'go-to-settings',
        title: () => t`Settings`,
        description: () => t`Configure organization settings`,
        icon: CogIcon,
        link: linkOptions({ to: '/settings/organization' }),
        group: () => t`Go to`,
        keywords: ['organization', 'user', 'preferences', 'account'],
    },
];
